import { useState, useLayoutEffect } from 'react';
import { TAILWIND_MD_MAX_PX, TAILWIND_MD_MIN_PX } from '@/shared/constants/breakpoints';

/**
 * Custom hook to detect if the current viewport matches a media query
 * @param query - Media query string (e.g., '(min-width: 768px)')
 * @returns boolean indicating if the media query matches
 */
export function useMediaQuery(query: string): boolean {
  // Always start with false for SSR compatibility
  // This ensures server and client render the same initially
  const [matches, setMatches] = useState(false);
  const [mounted, setMounted] = useState(false);

  // Use useLayoutEffect for synchronous state updates during hydration
  // This is necessary for SSR compatibility and prevents hydration mismatches
  useLayoutEffect(() => {
    // Mark as mounted after first render (client-side only)
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Required for SSR hydration
    setMounted(true);
    
    // Check if we're in the browser
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQuery = window.matchMedia(query);
    
    // Update with the actual value after mount
    setMatches(mediaQuery.matches);

    // Create event listener
    const handler = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handler);
      return () => mediaQuery.removeEventListener('change', handler);
    } 
    // Fallback for older browsers
    else {
      mediaQuery.addListener(handler);
      return () => mediaQuery.removeListener(handler);
    }
  }, [query]);

  // Return false during SSR and initial client render to prevent hydration mismatch
  // After mount, return the actual value
  return mounted ? matches : false;
}

/**
 * Hook to detect if we're on a mobile viewport (below md breakpoint)
 * Uses Tailwind's md breakpoint: 970px (max-width: 969px)
 */
export function useIsMobile(): boolean {
  return useMediaQuery(`(max-width: ${TAILWIND_MD_MAX_PX}px)`);
}

/**
 * Hook to detect if we're on a desktop viewport (md breakpoint and above)
 * Uses Tailwind's md breakpoint: 970px (so "Request desktop site" on mobile shows desktop)
 */
export function useIsDesktop(): boolean {
  return useMediaQuery(`(min-width: ${TAILWIND_MD_MIN_PX}px)`);
}

